import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Save, ImagePlus } from "lucide-react";

export const EditProductDialog = ({ product, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({
    title: product.title,
    description: product.description || "",
    price: product.price,
    stock: product.stock,
  });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState(product.image_url);

  const aiPhoto = async () => {
    setImgBusy(true);
    try {
      const { data } = await api.post("/products/bulk/generate-photo", { product_id: product.id });
      setImgUrl(data.image_url);
      toast.success("AI photo created and applied");
      onSaved?.({ ...product, image_url: data.image_url });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setImgBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/products/${product.id}`, {
        title: form.title,
        description: form.description,
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10),
      });
      toast.success("Product updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const aiRewrite = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/listing-assist", { title: form.title, notes: form.description });
      if (data.description) setForm((f) => ({ ...f, description: data.description }));
      toast.success("AI rewrote the description — adjust and save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit product</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {imgUrl ? (
              <img src={imgUrl.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${imgUrl}` : imgUrl} alt={form.title} className="h-14 w-14 rounded-md object-cover border border-[#27272A]" />
            ) : (
              <div className="h-14 w-14 rounded-md border border-dashed border-[#3f3f46] flex items-center justify-center text-neutral-600 text-[9px] uppercase">No photo</div>
            )}
            <button
              onClick={aiPhoto}
              disabled={imgBusy}
              data-testid="edit-ai-photo-btn"
              className="inline-flex items-center gap-1.5 text-xs text-[#007AFF] hover:underline disabled:opacity-50"
            >
              <ImagePlus className="h-3.5 w-3.5" /> {imgBusy ? "Creating photo…" : imgUrl ? "AI regenerate photo" : "AI generate photo"}
            </button>
          </div>
          <Input
            data-testid="edit-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-[#0A0A0A] border-[#27272A]"
            placeholder="Title"
          />
          <div>
            <Textarea
              data-testid="edit-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 1000) })}
              className="bg-[#0A0A0A] border-[#27272A] min-h-[110px]"
              placeholder="Description"
            />
            <button
              onClick={aiRewrite}
              disabled={aiBusy}
              data-testid="edit-ai-rewrite-btn"
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#FF9F0A] hover:underline disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" /> {aiBusy ? "Rewriting…" : "AI rewrite description"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Price ($)</div>
              <Input
                type="number" min="0.01" step="0.01"
                data-testid="edit-price-input"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] font-mono"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Stock</div>
              <Input
                type="number" min="0" step="1"
                data-testid="edit-stock-input"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] font-mono"
              />
            </div>
          </div>
          <Button
            onClick={save}
            disabled={busy || !form.title.trim()}
            data-testid="edit-save-btn"
            className="w-full h-10 rounded-full bg-white text-black hover:bg-neutral-200 font-semibold"
          >
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
